import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { topicMeta } from "@/lib/topicMeta";
import { Clock, ThumbsUp, ThumbsDown, ChevronRight, Loader2, FileQuestion, CheckCircle2 } from "lucide-react";

function ArticleBody({ text }) {
  const blocks = text.split("\n\n").filter(Boolean);
  return (
    <div className="article-body text-base sm:text-lg leading-relaxed text-slate-700">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        const isBulletGroup = lines.every((l) => l.trim().startsWith("- "));
        const isNumberStep = /^\d+\.\s/.test(lines[0]) && lines.length === 1;
        if (isBulletGroup) {
          return (
            <ul key={i} className="list-disc pl-6 space-y-1.5 mb-5">
              {lines.map((l, j) => (
                <li key={j}>{l.replace(/^-\s/, "")}</li>
              ))}
            </ul>
          );
        }
        // Short heading-like line (no trailing period, under ~60 chars)
        if (lines.length === 1 && block.length < 60 && !block.endsWith(".") && !isNumberStep) {
          return (
            <h3 key={i} className="font-heading text-xl font-semibold text-slate-900 mt-8 mb-3">
              {block}
            </h3>
          );
        }
        if (isNumberStep) {
          return (
            <h3 key={i} className="font-heading text-lg font-semibold text-slate-900 mt-7 mb-2">
              {block}
            </h3>
          );
        }
        return <p key={i}>{block}</p>;
      })}
    </div>
  );
}

export default function ArticleReadingPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [voted, setVoted] = useState(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    const prior = localStorage.getItem(`vote_${slug}`);
    setVoted(prior || null);
  }, [slug]);

  const vote = async (helpful) => {
    if (voted) return;
    const choice = helpful ? "yes" : "no";
    setVoted(choice);
    localStorage.setItem(`vote_${slug}`, choice);
    try {
      await api.post(`/articles/${slug}/feedback`, { helpful });
    } catch {
      /* non-blocking */
    }
  };

  if (loading)
    return (
      <div className="py-32 grid place-items-center text-slate-400">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );

  if (notFound || !article)
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <FileQuestion className="w-12 h-12 mx-auto text-slate-300" />
        <h1 className="font-heading text-2xl font-semibold mt-4 text-slate-800">Article not found</h1>
        <Link to="/" className="text-[#1A73E8] hover:underline mt-3 inline-block">Back to home</Link>
      </div>
    );

  const { Icon, color, bg } = topicMeta(article.topic_icon);

  return (
    <main data-testid="article-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <nav className="flex items-center flex-wrap gap-1.5 text-sm text-slate-500 mb-7" data-testid="article-breadcrumb">
        <Link to="/" className="hover:text-[#1A73E8]">Help Center</Link>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <Link to={`/topic/${article.topic_slug}`} className="hover:text-[#1A73E8]">{article.topic_name}</Link>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <span className="text-slate-800 font-medium line-clamp-1">{article.title}</span>
      </nav>

      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: bg, color }}>
          <Icon className="w-3.5 h-3.5" /> {article.topic_name}
        </span>
        <span className="text-xs font-semibold text-slate-400">{article.section}</span>
      </div>

      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
        {article.title}
      </h1>

      <div className="flex items-center gap-4 mt-4 pb-7 mb-8 border-b border-slate-200 text-sm text-slate-500">
        <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {article.read_time} min read</span>
        <span className="inline-flex items-center gap-1.5"><ThumbsUp className="w-4 h-4" /> {article.helpful_yes.toLocaleString()} found helpful</span>
      </div>

      <ArticleBody text={article.body} />

      {/* Feedback */}
      <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        {voted ? (
          <div data-testid="feedback-confirmation-msg" className="flex items-center gap-3 text-slate-700">
            <CheckCircle2 className="w-6 h-6 text-[#188038]" />
            <span className="font-medium">Thanks for your feedback — it helps us improve our guides.</span>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <span className="font-heading text-lg font-semibold text-slate-900">Was this article helpful?</span>
            <div className="flex items-center gap-3">
              <button
                data-testid="feedback-helpful-yes-btn"
                onClick={() => vote(true)}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-slate-200 font-semibold text-slate-700 hover:border-[#188038] hover:text-[#188038] hover:bg-green-50 transition-colors"
              >
                <ThumbsUp className="w-4 h-4" /> Yes
              </button>
              <button
                data-testid="feedback-helpful-no-btn"
                onClick={() => vote(false)}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-slate-200 font-semibold text-slate-700 hover:border-[#D93025] hover:text-[#D93025] hover:bg-red-50 transition-colors"
              >
                <ThumbsDown className="w-4 h-4" /> No
              </button>
            </div>
          </div>
        )}
      </div>

      <Link to={`/topic/${article.topic_slug}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1A73E8] hover:underline mt-8">
        More in {article.topic_name} <ChevronRight className="w-4 h-4" />
      </Link>
    </main>
  );
}
